import {
  SiCplusplus, SiPython, SiUnity, SiCmake,
  SiJavascript, SiHtml5, SiCss3, SiPhp, SiJquery,
} from 'react-icons/si';
import { FaCode } from 'react-icons/fa';

const EXP_TECH_ICONS = {
  'C++':        { Icon: SiCplusplus,  color: '#00599C' },
  'CMake':      { Icon: SiCmake,      color: '#064F8C' },
  'Python':     { Icon: SiPython,     color: '#3776AB' },
  // C# has no Simple Icons entry — FaCode fallback used
  'Unity':      { Icon: SiUnity,      color: '#e2e8f0' },
  'JavaScript': { Icon: SiJavascript, color: '#F7DF1E' },
  'HTML':       { Icon: SiHtml5,      color: '#E34F26' },
  'CSS':        { Icon: SiCss3,       color: '#1572B6' },
  'PHP':        { Icon: SiPhp,        color: '#777BB4' },
  'jQuery':     { Icon: SiJquery,     color: '#0769AD' },
};

const EXPERIENCES = [
  {
    period: "Dec '24 – Feb '25",
    role: 'Freelance Software Engineer',
    company: 'TrinityXR',
    color: '#00f5ff',
    description: (
      <>
        Bridged a <strong className="text-slate-200">compatibility gap</strong> between the open-source{' '}
        <strong className="text-slate-200">Mediapipe framework</strong> and Unity by designing a{' '}
        <strong className="text-slate-200">real-time human tracking system</strong> that accurately maps
        pose and face data — directly enabling next-gen{' '}
        <strong className="text-slate-200">AR/VR features</strong> within their flagship application.
      </>
    ),
    stack: ['C++', 'CMake', 'Python', 'C#', 'Unity', 'Mediapipe'],
  },
  {
    period: "Mar '24 – Oct '24",
    role: 'Front End Developer',
    company: 'Business Quant',
    color: '#ff00de',
    description: (
      <>
        Built <strong className="text-slate-200">custom WordPress plugins</strong> in PHP and crafted
        responsive UIs with HTML, CSS and JavaScript. Designed and customised{' '}
        <strong className="text-slate-200">WordPress themes</strong>, enhanced interactivity with{' '}
        <strong className="text-slate-200">jQuery</strong>, and owned testing &amp; debugging to ship
        smooth user experiences.
      </>
    ),
    stack: ['JavaScript', 'HTML', 'PHP', 'jQuery', 'CSS'],
  },
];

const Experience = () => (
  <section id="experience" className="py-20 px-2">
    {/* Heading */}
    <div className="reveal mb-14">
      <p className="text-[#ff00de] text-xs font-mono tracking-[0.25em] uppercase mb-2">
        // where I've worked
      </p>
      <h2
        className="section-title text-4xl font-black mb-4"
        style={{
          background: 'linear-gradient(135deg, #00f5ff, #ff00de)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        Experience
      </h2>
      <div className="neon-line-pink w-28" />
    </div>

    {/* Timeline */}
    <div className="relative">
      {/* Vertical spine */}
      <div
        className="timeline-line absolute left-6 top-2 bottom-2 hidden sm:block"
        style={{ marginLeft: -1 }}
      />

      <div className="flex flex-col gap-12">
        {EXPERIENCES.map((exp, i) => (
          <div
            key={i}
            className="relative flex flex-col sm:flex-row gap-6 sm:gap-10 reveal"
            style={{ transitionDelay: `${i * 0.15}s` }}
          >
            {/* Timeline dot */}
            <div className="hidden sm:flex flex-col items-center" style={{ minWidth: 12 }}>
              <div
                className="timeline-dot mt-1"
                style={{
                  borderColor: exp.color,
                  boxShadow: `0 0 10px ${exp.color}, 0 0 20px ${exp.color}44`,
                }}
              />
            </div>

            {/* Date badge */}
            <div className="sm:w-44 flex-shrink-0">
              <span
                className="inline-block text-xs font-mono tracking-widest px-3 py-1.5 rounded-md"
                style={{
                  color: exp.color,
                  background: `${exp.color}14`,
                  border: `1px solid ${exp.color}40`,
                }}
              >
                {exp.period}
              </span>
            </div>

            {/* Card */}
            <div
              className="flex-1 neon-card p-6 transition-all duration-300"
              style={{ borderColor: `${exp.color}30` }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${exp.color}80`;
                e.currentTarget.style.boxShadow = `0 0 20px ${exp.color}22`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${exp.color}30`;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h3 className="text-lg font-bold text-slate-100 mb-0.5">{exp.role}</h3>
              <p className="text-sm font-mono mb-4" style={{ color: exp.color }}>
                @ {exp.company}
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                {exp.description}
              </p>

              {/* Tech stack */}
              <div className="flex flex-wrap gap-2">
                {exp.stack.map((tech, ti) => {
                  const info = EXP_TECH_ICONS[tech];
                  const Icon = info?.Icon;
                  return (
                  <span
                    key={ti}
                    className="tech-badge inline-flex items-center gap-1.5"
                    style={{ borderColor: `${exp.color}35` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = exp.color;
                      e.currentTarget.style.borderColor = exp.color;
                      e.currentTarget.style.background = `${exp.color}14`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#94a3b8';
                      e.currentTarget.style.borderColor = `${exp.color}35`;
                      e.currentTarget.style.background = 'rgba(0,245,255,0.08)';
                    }}
                  >
                    {Icon
                      ? <Icon size={11} style={{ color: info.color, flexShrink: 0 }} />
                      : <FaCode size={10} style={{ color: exp.color, opacity: 0.7, flexShrink: 0 }} />
                    }
                    {tech}
                  </span>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Experience;
